import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AuthDrawerProvider } from './context/AuthDrawerContext';
import { SiteSettingsProvider, useSiteSettings } from './context/SiteSettingsContext';
import { RadioProvider } from './context/RadioContext';
import { Toaster } from './components/ui/toaster';
import HomePage from './pages/HomePage';
import GoSoupHome from './pages/GoSoupHome';
import ShopPage from './pages/ShopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import LegacyProductRedirectPage from './pages/LegacyProductRedirectPage';
import LegacyProductSlugRedirectPage from './pages/LegacyProductSlugRedirectPage';
import ContactPage from './pages/ContactPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import LoginPage from './pages/LoginPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ResearchLibraryPage from './pages/research/ResearchLibraryPage';
import ResearchArticlePage from './pages/research/ResearchArticlePage';
import FAQPage from './pages/FAQPage';
import AboutUsPage from './pages/AboutUsPage';
import AdminLayout from './pages/AdminLayout';
import AdminSettingsLayout from './pages/admin/AdminSettingsLayout';
import QrBuilderLayout from './pages/admin/QrBuilderLayout';
import DevSettingsLayout from './pages/dev/DevSettingsLayout';
import MaintenancePage from './pages/MaintenancePage';

// Legal Pages
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsConditionsPage from './pages/legal/TermsConditionsPage';
import AccessibilityPage from './pages/legal/AccessibilityPage';

// Location Pages (SEO)
import LocationPage from './pages/LocationPage';
import DevLocationPreview from './pages/dev/DevLocationPreview';

// User Portal
import UserPortal from './pages/UserPortal';

// New 123Bots Pages
import ScheduleDemoPage from './pages/ScheduleDemoPage';
import ShowcaseDirectoryPage from './pages/ShowcaseDirectoryPage';
import FreeToolsPage from './pages/FreeToolsPage';
import BackgroundRemoverPage from './pages/BackgroundRemoverPage';
import QrCodeGeneratorPage from './pages/QrCodeGeneratorPage';
import EmailSignaturesPage from './pages/EmailSignaturesPage';
import TransferGate from './tools/transfer/TransferGate';
import SiteTemplateGuard from './tools/transfer/SiteTemplateGuard';
import TemplateFrame from './tools/transfer/TemplateFrame';
import { useSiteFeatureFlags } from './hooks/useSiteFeatureFlags';
import TransferContentPage from './pages/TransferContentPage';
import HomeSwitch from './tools/transfer/HomeSwitch';
import TransferPage from './pages/TransferPage';
import TransferDownloadPage from './pages/TransferDownloadPage';
import TransferEmbedPage from './pages/TransferEmbedPage';
import CategoryLandingPage from './pages/CategoryLandingPage';
import PublicBookingPage from './pages/PublicBookingPage';
import ServiceRequestPage from './pages/ServiceRequestPage';
import IndustryPage from './pages/IndustryPage';
import QuoteSigningPage from './pages/quotes/QuoteSigningPage';
import EventsIndexPage from './pages/EventsIndexPage';
import EventCategoryPage from './pages/EventCategoryPage';
import EventDetailPage from './pages/EventDetailPage';
import EventConfirmationPage from './pages/EventConfirmationPage';
import TicketViewPage from './pages/TicketViewPage';
import ActivitiesDirectoryPage from './pages/activities/ActivitiesDirectoryPage';
import ActivitiesByCompanyPage from './pages/activities/ActivitiesByCompanyPage';
import ToursChartersInvoiceView from './pages/activities/ToursChartersInvoiceView';
import ActivityListPage from './pages/activities/ActivityListPage';
import ActivityDetailPage from './pages/activities/ActivityDetailPage';

// Age Verification
import AgeVerificationModal from './components/AgeVerificationModal';

// Chat Widget (Public Pages Only)
import ChatWidget from './components/ChatWidget';

// PWA Install Banner (Public Pages Only)
import InstallPWABanner from './components/InstallPWABanner';

import ChatPopout from './pages/admin/ChatPopout';

const A2G_ANALYTICS_SCRIPT_ID = 'ZwSg9rf6GA';
const A2G_ANALYTICS_SRC = 'https://a2ganalytics.com/js/script.js';

const A2GAnalyticsScript = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const isPublicRoute = !pathname.startsWith('/admin') && !pathname.startsWith('/dev');
    const existingScript = document.getElementById(A2G_ANALYTICS_SCRIPT_ID);

    if (!isPublicRoute) {
      return;
    }

    if (existingScript) {
      return;
    }

    const analyticsScript = document.createElement('script');
    analyticsScript.id = A2G_ANALYTICS_SCRIPT_ID;
    analyticsScript.src = A2G_ANALYTICS_SRC;
    analyticsScript.async = true;
    analyticsScript.defer = true;
    analyticsScript.setAttribute('data-host', 'https://a2ganalytics.com');
    analyticsScript.setAttribute('data-dnt', 'false');
    document.head.appendChild(analyticsScript);
  }, [pathname]);

  return null;
};

// Scroll to top on route change — unless the URL names an anchor.
//
// A router navigation does not trigger the browser's own hash scrolling, so
// without this a link like /#estimator would land on the page and then get
// yanked back to the top by the scroll-to-top below.
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }

    // Two problems to survive here:
    //  1. The target is usually not in the DOM yet — sections mount after
    //     their data resolves, so one frame is not enough.
    //  2. Once it is, images and later sections are still loading, so the
    //     target keeps moving down the page. Scrolling once lands short.
    // So: wait for the element, scroll, then keep correcting until its
    // position holds still, with a deadline so this can never run on forever.
    const id = hash.slice(1);
    const deadline = Date.now() + 5000;
    let timer = 0;
    let lastTop = null;
    let settled = 0;

    const tick = () => {
      const el = document.getElementById(id);

      if (!el) {
        if (Date.now() < deadline) timer = setTimeout(tick, 100);
        return;
      }

      const top = Math.round(el.getBoundingClientRect().top);

      // Within a pixel or two of the top counts as arrived. Require two
      // consecutive stable readings so a mid-animation sample does not pass.
      if (Math.abs(top) <= 2) {
        settled += 1;
        if (settled >= 2) return;
      } else {
        settled = 0;
        // Re-scroll only when the target actually moved, otherwise the smooth
        // animation gets restarted every tick and never arrives.
        if (top !== lastTop) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          lastTop = top;
        }
      }

      if (Date.now() < deadline) timer = setTimeout(tick, 100);
    };

    tick();
    return () => clearTimeout(timer);
  }, [pathname, hash]);

  return null;
};

// Layout wrapper for public pages
const PublicLayout = ({ children }) => {
  const location = useLocation();
  const hideNav = location.pathname === '/login' || 
    location.pathname === '/register' ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/dev');

  // /embed/* pages are framed on a client's own site: no site chat or install prompt there.
  const isEmbed = location.pathname.startsWith('/embed/');
  const { transfer_site_enabled } = useSiteFeatureFlags();

  return (
    <>
      <main className="min-h-screen bg-bots-dark">{children}</main>
      {!isEmbed && !transfer_site_enabled && <ChatWidget />}
      {!isEmbed && <InstallPWABanner />}
    </>
  );
};

// Maintenance Mode Guard - wraps public routes
const MaintenanceGuard = ({ children }) => {
  const { maintenanceMode, loading } = useSiteSettings();
  const { isAuthenticated, user } = useAuth();
  
  // Show nothing while loading settings
  if (loading) {
    return (
      <div className="min-h-screen bg-void-base flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  // Check if user is admin (can bypass maintenance)
  const isAdmin = isAuthenticated && (user?.role === 'admin' || user?.role === 'super_admin');
  
  // Show maintenance page for non-admins when maintenance mode is enabled
  if (maintenanceMode && !isAdmin) {
    return <MaintenancePage />;
  }
  
  return children;
};

const ImpersonationBanner = () => {
  const { isImpersonating, impersonationAdmin, exitImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!isImpersonating) {
    return null;
  }

  const handleExitImpersonation = async () => {
    const result = await exitImpersonation();
    if (result.success) {
      if (!location.pathname.startsWith('/admin')) {
        navigate('/admin/user-management/customers');
      }
    }
  };

  return (
    <div className="sticky top-0 z-[60] bg-amber-500 text-black shadow" data-testid="impersonation-session-banner">
      <div className="max-w-[1600px] mx-auto px-4 py-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium" data-testid="impersonation-session-label">
          Impersonating customer account
          {impersonationAdmin?.email ? ` • Admin: ${impersonationAdmin.email}` : ''}
        </p>
        <button
          type="button"
          onClick={handleExitImpersonation}
          className="px-3 py-1 text-sm rounded bg-black text-white hover:bg-gray-900 transition-colors"
          data-testid="exit-impersonation-button"
        >
          Exit Impersonation
        </button>
      </div>
    </div>
  );
};

// Inner app component that uses auth context
const AppContent = () => {
  // An embed is framed fresh on a client's page and must be transparent so their page shows around it.
  // (The Router lives inside this component, so read the path directly.)
  const isEmbedPage = window.location.pathname.startsWith('/embed/');
  return (
    <div className={isEmbedPage ? 'App' : 'App bg-void-base min-h-screen'}>
      <RadioProvider>
        <BrowserRouter>
          <AuthDrawerProvider>
          <A2GAnalyticsScript />
          <ImpersonationBanner />
          <ScrollToTop />
          <AgeVerificationModal />
          <Routes>
          {/* Dev Settings Routes (Super Admin Only) */}
          <Route path="/dev/settings/*" element={<DevSettingsLayout />} />
          
          {/* Dev Location Preview - Full page (no sidebar) */}
          <Route path="/dev/location-preview" element={<DevLocationPreview />} />
          
          {/* Admin Settings Routes */}
          <Route path="/admin/settings/*" element={<AdminSettingsLayout />} />

          {/* QR Builder - own dashboard, own sidebar */}
          <Route path="/admin/qr-builder/*" element={<QrBuilderLayout />} />

          {/* Public portfolio directory */}
          <Route path="/apps" element={<TransferGate flag="showcase_enabled"><SiteTemplateGuard><ShowcaseDirectoryPage /></SiteTemplateGuard></TransferGate>} />
          <Route path="/tools" element={<SiteTemplateGuard><FreeToolsPage /></SiteTemplateGuard>} />
          <Route path="/tools/background-remover" element={<SiteTemplateGuard><BackgroundRemoverPage /></SiteTemplateGuard>} />
          <Route path="/tools/qr-code" element={<TransferGate flag="portal_qr_codes_enabled"><SiteTemplateGuard><QrCodeGeneratorPage /></SiteTemplateGuard></TransferGate>} />
          <Route path="/tools/email-signatures" element={<SiteTemplateGuard><EmailSignaturesPage /></SiteTemplateGuard>} />

          {/* File Transfer (feature-flagged: TransferGate redirects home when the flag is off) */}
          <Route path="/transfer" element={<TransferGate><SiteTemplateGuard><TransferPage /></SiteTemplateGuard></TransferGate>} />
          <Route path="/t/:token" element={<TransferGate><TransferDownloadPage /></TransferGate>} />
          <Route path="/embed/transfer" element={<TransferGate flag="transfer_embed_enabled"><TransferEmbedPage /></TransferGate>} />
          <Route path="/p/:slug" element={<TransferGate><TransferContentPage /></TransferGate>} />
          
          {/* Chat Popout - Standalone window (no sidebar) */}
          <Route path="/admin/chat/:chatId" element={<ChatPopout />} />
          
          {/* Admin Routes */}
          <Route path="/admin/*" element={<AdminLayout />} />
          
          {/* Location Pages - Standalone (no PublicLayout wrapper) - also protected by maintenance */}
          <Route path="/locations/:slug" element={
            <MaintenanceGuard>
              <SiteTemplateGuard><LocationPage /></SiteTemplateGuard>
            </MaintenanceGuard>
          } />
          
          {/* Public Routes - Protected by Maintenance Guard */}
            <Route path="/*" element={
              <MaintenanceGuard>
                <PublicLayout>
                  <SiteTemplateGuard>
                  <Routes>
                  <Route path="/" element={<HomeSwitch fallback={<GoSoupHome />} />} />
                  <Route path="/shop" element={<CategoryLandingPage />} />
                  <Route path="/categories" element={<CategoryLandingPage />} />
                  <Route path="/shop/products" element={<ShopPage />} />
                  <Route path="/shop/:legacySlug" element={<LegacyProductSlugRedirectPage />} />
                  <Route path="/shop/:categorySlug/:productSlug" element={<ProductDetailPage />} />
                  <Route path="/product/:productId" element={<LegacyProductRedirectPage />} />
                  <Route path="/schedule-a-demo" element={<ScheduleDemoPage />} />
                  <Route path="/service-request" element={<ServiceRequestPage />} />
                  <Route path="/events" element={<EventsIndexPage />} />
                  <Route path="/events/category/:catSlug" element={<EventCategoryPage />} />
                  <Route path="/events/confirmation" element={<EventConfirmationPage />} />
                  <Route path="/events/ticket/:code" element={<TicketViewPage />} />
                  <Route path="/events/:slug" element={<EventDetailPage />} />
                  <Route path="/activities" element={<ActivitiesDirectoryPage />} />
                  <Route path="/activities/companies" element={<ActivitiesByCompanyPage />} />
                  <Route path="/activities/category/:slug" element={<ActivityListPage mode="category" />} />
                  <Route path="/activities/company/:slug" element={<ActivityListPage mode="company" />} />
                  <Route path="/activities/view/:slug" element={<ActivityDetailPage />} />
                  <Route path="/invoice/tours-charters/:invoiceId" element={<ToursChartersInvoiceView />} />
                    <Route path="/contact" element={<ContactPage />} />
                  <Route path="/industries/:industrySlug" element={<IndustryPage />} />
                    <Route path="/booking/:bookingSlug" element={<PublicBookingPage />} />
                    <Route path="/book/:bookingSlug" element={<PublicBookingPage />} />
                    <Route path="/sign/:quoteId" element={<QuoteSigningPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/accept-invite" element={<AcceptInvitePage />} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/research" element={<ResearchLibraryPage />} />
                  <Route path="/research/:slug" element={<ResearchArticlePage />} />
                  <Route path="/privacy-policy" element={<TemplateFrame><PrivacyPolicyPage /></TemplateFrame>} />
                  <Route path="/terms-conditions" element={<TemplateFrame><TermsConditionsPage /></TemplateFrame>} />
                  <Route path="/accessibility" element={<TemplateFrame><AccessibilityPage /></TemplateFrame>} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/about" element={<AboutUsPage />} />
                  <Route path="/account/*" element={<UserPortal />} />
                  <Route path="/storage/*" element={<Navigate to="/" replace />} />
                  <Route path="/rv-repair/*" element={<Navigate to="/" replace />} />
                  <Route path="/employment/*" element={<Navigate to="/" replace />} />
                  <Route path="/job-application/*" element={<Navigate to="/" replace />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                  </SiteTemplateGuard>
                </PublicLayout>
              </MaintenanceGuard>
            } />
          </Routes>
          </AuthDrawerProvider>
        </BrowserRouter>
      </RadioProvider>
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <SiteSettingsProvider>
          <AppContent />
        </SiteSettingsProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
